import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/store.js';
import { useBilling } from '../lib/queries.js';
import { PlanCards } from './PlanCards.js';
import { num } from '../lib/format.js';
import { useT } from '../lib/i18n.js';

/**
 * Die Paywall.
 *
 * Vorher lief eine Tarifgrenze als roter Text vorbei, „Der Tarif Free
 * erlaubt 1 Projekte", und war zwei Sekunden später weg. Der Nutzer stand
 * mit einem Fehler da, ohne zu wissen, was ihn ein zweites Projekt kostet.
 *
 * Jetzt öffnet derselbe Fall dieses Fenster: Ganz oben steht wörtlich, was
 * nicht ging und warum, direkt darunter liegen die Tarife mit dem passenden
 * hervorgehoben. Fehler und Lösung im selben Blick, das ist der Moment, in
 * dem ein Upgrade Sinn ergibt.
 */

const LIMIT_COPY: Record<string, { icon: string; title: string; body: string }> = {
  projects: {
    icon: '▦',
    title: 'Dieses Projekt braucht mehr Platz',
    body: 'Jedes Projekt ist eine eigene App mit eigenen Accounts, eigenem Briefing und eigenem Zeitplan. Wie viele davon parallel laufen dürfen, hängt am Tarif.',
  },
  accounts: {
    icon: '⇄',
    title: 'Mehr Accounts pro Projekt',
    body: 'TikTok, Instagram und Facebook zählen einzeln. Wer mehrere Kanäle aus einem Projekt bespielt, braucht entsprechend Plätze.',
  },
  agents: {
    icon: '✦',
    title: 'Ein zweites Rezept',
    body: 'Ein Rezept legt fest, wie ein Beitrag aussieht: Stil, Ziel, Tonfall, Modelle. Mit einem sieht dein Feed immer gleich aus. Erst ab dem zweiten kannst du vergleichen — Bilder gegen Video, locker gegen sachlich — und siehst schwarz auf weiß, welches Rezept mehr Reichweite je Credit bringt.',
  },
  credits: {
    icon: '✦',
    title: 'Das Guthaben reicht nicht',
    body: 'Generieren, Rendern und Veröffentlichen kosten Credits. Ein Monatstarif schreibt dir jeden Monat welche gut. Das ist günstiger als einzeln nachladen.',
  },
};

export function Paywall() {
  const open = useAuth((s) => s.paywall.open);
  // Erst wenn die Paywall wirklich auftaucht, wird der Abrechnungsstand
  // geladen, sonst hinge an jeder Seite eine Abfrage, die niemand sieht.
  return open ? <PaywallDialog /> : null;
}

function PaywallDialog() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const paywall = useAuth((s) => s.paywall);
  const close = useAuth((s) => s.closePaywall);
  const org = useAuth((s) => s.org);
  const { data: billing } = useBilling();

  const copy = LIMIT_COPY[paywall.limit ?? 'projects'] ?? LIMIT_COPY.projects;
  const planName = billing?.plan.name ?? org?.planName ?? org?.plan ?? 'Free';

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="modal" style={{ maxWidth: 1060 }} role="dialog" aria-modal="true">
        {/* Kopf: was nicht ging */}
        <div style={{ padding: '20px 22px 0' }}>
          <div className="row" style={{ alignItems: 'flex-start', gap: 14 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 'var(--r-lg)', flex: 'none',
                display: 'grid', placeItems: 'center', fontSize: 18,
                background: 'var(--warn-soft)', color: 'var(--warn)',
              }}
            >
              {copy.icon}
            </div>
            <div className="grow">
              <div style={{ fontSize: 20, fontWeight: 650, letterSpacing: '-.02em' }}>{t(copy.title)}</div>
              <div className="muted" style={{ fontSize: 13.5, marginTop: 4, lineHeight: 1.55, maxWidth: '68ch' }}>
                {t(copy.body)}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={close} aria-label={t('Schließen')}>✕</button>
          </div>

          {/* Der Originaltext aus dem Backend, er nennt Tarif und Zahlen */}
          {paywall.reason && (
            <div className="note note-warn" style={{ marginTop: 15 }}>
              <span className="note-icon">!</span>
              <div>
                <strong>{paywall.reason}</strong>
                <div className="dim" style={{ marginTop: 3, fontSize: 12.5 }}>
                  {t('Aktueller Tarif:')} <strong>{planName}</strong>
                  {paywall.limit === 'credits' && billing
                    ? t(' · {n} Credits verfügbar', { n: num(billing.available) })
                    : ''}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tarife */}
        <div style={{ padding: '18px 22px 8px' }}>
          <div className="section-label" style={{ marginBottom: 11 }}>
            {paywall.suggestedPlan ? t('Der nächste Schritt, und was danach kommt') : t('Tarife')}
          </div>
          <PlanCards
            currentPlan={billing?.plan.key ?? org?.plan}
            hasSubscription={!!billing && !['none', 'canceled', 'cancelled', 'incomplete_expired'].includes(billing.subscriptionStatus)}
            highlight={paywall.suggestedPlan}
            compact
            onPurchaseStart={close}
          />
        </div>

        <div
          className="row wrap"
          style={{ padding: '14px 22px 18px', gap: 10, borderTop: '1px solid var(--line)', marginTop: 10 }}
        >
          <div className="dim grow" style={{ fontSize: 12.5, lineHeight: 1.55, minWidth: 220 }}>
            {t('Lieber einmalig nachladen statt Abo? Credit-Pakete laufen nicht ab und lösen Guthaben-Grenzen, Projekt-Grenzen aber nicht.')}
          </div>
          <button className="btn btn-ghost" onClick={close}>{t('Später')}</button>
          <button
            className="btn btn-secondary"
            onClick={() => { close(); navigate('/billing#packs', { state: { from: location.pathname } }); }}
          >
            {t('Credit-Pakete ansehen')}
          </button>
        </div>
      </div>
    </div>
  );
}
